import { ChartOptions, type TooltipItem } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { EmptyLineOrBarChart } from './EmptyLineOrBarChart';
import { useTranslation } from 'react-i18next';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';

const BAR_BORDER_WIDTH = 1.5;
const BAR_BORDER_RADIUS = 4;
const BORDER_DARK_BLUE_COLOR = '#597b82';

export type DatasetPoint = {
  label: string;
  value: string;
  barColor: string;
  borderColor: string;
};

type DataSetItem = {
  label: string;
  data: DatasetPoint[];
  hidden: boolean;
};

type BarChartProps = {
  xAxisLabels: string[];
  datasets: DataSetItem[];
  yAxisLabel: string;
  barsStacked?: boolean;
  tooltipLabelPriority?: Record<string, number>;
  tooltipValueFormatter?: (value: number, unit: string) => string;
};

export const BarChart = ({
  xAxisLabels,
  datasets,
  yAxisLabel,
  barsStacked = true,
  tooltipLabelPriority,
  tooltipValueFormatter,
}: BarChartProps) => {
  const { t } = useTranslation();

  const data = {
    labels: xAxisLabels,
    datasets: datasets.map((set) => ({
      label: set.label,
      data: set.data.map((item) => (set.hidden ? 0 : item.value)),
      backgroundColor: set.data.map((item) => item.barColor),
      borderRadius: BAR_BORDER_RADIUS,
      borderColor: set.data.map((item) => item.borderColor),
      borderWidth: BAR_BORDER_WIDTH,
      minBarLength: set.hidden ? 0 : 2,
    })),
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        itemSort: (a: TooltipItem<'bar'>, b: TooltipItem<'bar'>) => {
          if (!tooltipLabelPriority) {
            return 0;
          }

          const la = tooltipLabelPriority[a.dataset.label ?? ''] ?? 50;
          const lb = tooltipLabelPriority[b.dataset.label ?? ''] ?? 50;
          return la - lb;
        },
        callbacks: {
          title: (tooltipItems) => {
            const datasetIndex = tooltipItems[0].datasetIndex;
            const index = tooltipItems[0].dataIndex;

            const dateString = datasets[datasetIndex].data[index].label;
            const date = new Date(dateString);

            return date.toLocaleDateString(globalThis.navigator.language, {
              month: 'long',
              year: 'numeric',
            });
          },
          label: (tooltipItem) => {
            const totalDataset = datasets.find((set) => set.label === 'Total');
            const totalValue = totalDataset?.data[tooltipItem.dataIndex].value;

            const datasetLabel = tooltipItem.dataset.label;
            const unit = yAxisLabel ? ` ${yAxisLabel}` : '';

            const value =
              datasetLabel?.toLowerCase() === 'total' && totalValue
                ? +totalValue
                : +(tooltipItem.raw as string);

            const valueToDisplay = tooltipValueFormatter
              ? tooltipValueFormatter(value, yAxisLabel)
              : `${value}${unit}`;

            return `${datasetLabel}: ${valueToDisplay}`;
          },
          labelColor: (context) => {
            const dataset = context.chart.data.datasets[context.datasetIndex];

            const barColor: string = Array.isArray(dataset.backgroundColor)
              ? dataset.backgroundColor[context.dataIndex]
              : dataset.backgroundColor;

            const borderColor = dataset.borderColor;

            const isTotal = dataset.label?.toLowerCase() === 'total';

            return {
              borderColor: isTotal ? BORDER_DARK_BLUE_COLOR : (borderColor as string),
              backgroundColor: isTotal ? BORDER_DARK_BLUE_COLOR : barColor,
            };
          },
        },
      },
    },
    scales: {
      y: {
        stacked: barsStacked,
        title: {
          display: false,
        },
        ticks: {
          precision: 0,
          callback: (value: string | number) => {
            return `${toLocaleBrowserSettings(Number.parseFloat(value.toString()))} ${yAxisLabel}`;
          },
        },
      },
      x: {
        stacked: barsStacked,
        grid: {
          display: false,
        },
        ticks: {
          callback: function (val) {
            if (typeof val !== 'number') return val;
            const label = this.getLabelForValue(val);
            return label.split('\n');
          },
        },
      },
    },
  };

  if (!datasets.length) {
    return (
      <EmptyLineOrBarChart
        as={Bar}
        emptyChartText={t('common.noResults')}
        xAxisLabels={xAxisLabels}
      />
    );
  }

  return <Bar data={data} options={options} />;
};
